// Hash-based router with parameterized routes (e.g. "/tenancies/:id").
// Routes resolve to a factory `() => Component`. A `guard(route, ctx)` may
// short-circuit navigation by returning another path.

export class Router {
  constructor({ routes, outlet, guard }) {
    this.routes = routes.map((r) => ({ ...r, regex: toRegex(r.path), keys: extractKeys(r.path) }));
    this.outlet = outlet;
    this.guard = guard || (() => null);
    this.current = null;
    this._listeners = new Set();
  }

  start() {
    window.addEventListener('hashchange', () => this._resolve());
    this._resolve();
  }

  navigate(path, { replace = false } = {}) {
    const target = '#' + path;
    if (replace) {
      const url = new URL(window.location.href);
      url.hash = target;
      window.history.replaceState(null, '', url.toString());
      this._resolve();
    } else {
      window.location.hash = target;
    }
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _resolve() {
    const path = (window.location.hash || '#/').slice(1) || '/';
    const [pathname, queryString] = path.split('?');
    const query = Object.fromEntries(new URLSearchParams(queryString || ''));

    const match = this._match(pathname);
    if (!match) {
      this.outlet.innerHTML = '<div class="empty">Page not found</div>';
      return;
    }

    const ctx = { path: pathname, params: match.params, query };
    const redirect = this.guard(match.route, ctx);
    if (redirect) {
      this.navigate(redirect, { replace: true });
      return;
    }

    if (this.current && typeof this.current.destroy === 'function') {
      this.current.destroy();
    }

    const component = match.route.component(ctx);
    component.render(this.outlet);
    this.current = component;
    this._listeners.forEach((fn) => fn(ctx));
  }

  _match(pathname) {
    for (const route of this.routes) {
      const m = pathname.match(route.regex);
      if (!m) continue;
      const params = {};
      route.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
      return { route, params };
    }
    return null;
  }
}

function toRegex(path) {
  const pattern = path
    .replace(/\/$/, '')
    .replace(/[.+*?^${}()|[\]\\]/g, '\\$&')
    .replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, '([^/]+)');
  return new RegExp('^' + (pattern || '\\/') + '\\/?$');
}

function extractKeys(path) {
  return [...path.matchAll(/:([a-zA-Z][a-zA-Z0-9_]*)/g)].map((m) => m[1]);
}
